import { can, type Tier } from '@feral/shared/client'
import { IconButton, Icons } from '@feral/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSession } from '../../lib/session.js'
import {
  setPersonFavorite,
  setSpotFavorite,
  useIsFavorite,
  useIsSpotFavorite,
  useMayFavoriteSpots,
} from './keys.js'

/**
 * The star (decision 105): favorite a person or a spot from wherever they show, the Wall's rows and
 * every tile, as from a card or a profile. Flipped at once, put back if the server says no; filled in
 * your own color when on.
 */
function Star({
  on,
  name,
  size,
  className,
  onToggle,
}: {
  on: boolean
  name: string
  size: number
  className?: string
  onToggle: (next: boolean) => Promise<void>
}) {
  const [pending, setPending] = useState<boolean | null>(null)
  const shown = pending ?? on
  const label = shown ? `Remove ${name} from favorites` : `Add ${name} to favorites`
  return (
    <IconButton
      bare
      className={className}
      aria-label={label}
      aria-pressed={shown}
      title={shown ? 'Remove from favorites' : 'Add to favorites'}
      style={{ color: shown ? 'var(--you)' : undefined }}
      onClick={async (e) => {
        // Inside a tile or a row: the star alone, never the tile's own open.
        e.stopPropagation()
        const next = !shown
        setPending(next)
        try {
          await onToggle(next)
        } catch {
          // put back: the list is as it was
        } finally {
          setPending(null)
        }
      }}
    >
      {Icons.star(shown, size)}
    </IconButton>
  )
}

/**
 * A person's star, for vetted members (policy `favorite`) and never on yourself or a silhouette.
 * `initial` is what the map or the Wall said, used until your favorites list is in.
 */
export function PersonStar({
  id,
  tier,
  name,
  initial,
  size = 18,
  className,
}: {
  id: string | null | undefined
  tier: Tier
  name: string
  initial?: boolean
  size?: number
  className?: string
}) {
  const qc = useQueryClient()
  const me = useSession((s) => s.me)
  const listed = useIsFavorite(id)
  if (!id || !me || me.id === id) return null
  if (!can({ id: me.id, state: me.state }, 'favorite', { id, state: tier }).allow) return null
  return (
    <Star
      on={listed ?? !!initial}
      name={name}
      size={size}
      className={className}
      onToggle={(next) => setPersonFavorite(qc, id, next)}
    />
  )
}

/** A spot's star (decision 59): vetted members only, as a person's (issue 8). */
export function SpotStar({
  id,
  name,
  initial,
  size = 18,
  className,
}: {
  id: string
  name: string
  initial?: boolean
  size?: number
  className?: string
}) {
  const qc = useQueryClient()
  const may = useMayFavoriteSpots()
  const listed = useIsSpotFavorite(id)
  if (!may) return null
  return (
    <Star
      on={listed ?? !!initial}
      name={name}
      size={size}
      className={className}
      onToggle={(next) => setSpotFavorite(qc, id, next)}
    />
  )
}
